import { RefObject, useLayoutEffect, useRef } from 'react'

import { Cursor } from '../context/DataGridContext'
import { toCellId } from '../utils'

export type RecalculatePositionOnNavigationProps = {
    cursor?: Cursor
    firstColumnWidth: number
    headerRef: RefObject<HTMLDivElement | null>
    dataGridRef: RefObject<HTMLDivElement | null>
    footerRef: RefObject<HTMLDivElement | null>
}

export function useRecalculatePositionOnNavigation({ cursor, firstColumnWidth, headerRef, dataGridRef, footerRef }: RecalculatePositionOnNavigationProps) {
    const previousCursorIdRef = useRef<string | undefined>(undefined)

    useLayoutEffect(() => {
        const cursorId = cursor ? toCellId(cursor) : undefined

        if (!cursorId) {
            previousCursorIdRef.current = undefined
            return
        }

        // Selection consumers can recreate cursor objects; only navigation to another cell should move the viewport.
        if (previousCursorIdRef.current === cursorId) return

        const cell = document.getElementById(cursorId)
        if (!cell) return

        const headerRect = headerRef.current?.getBoundingClientRect()
        const footerRect = footerRef.current?.getBoundingClientRect()
        const dataGrid = dataGridRef.current
        if (!headerRect || !footerRect || !dataGrid) return

        previousCursorIdRef.current = cursorId

        const cellRect = cell.getBoundingClientRect()

        let nextScrollTop = dataGrid.scrollTop
        let nextScrollLeft = dataGrid.scrollLeft

        if (cellRect.bottom > footerRect.top) {
            nextScrollTop += cellRect.bottom - footerRect.top
        }

        if (cellRect.right > dataGrid.clientWidth) {
            nextScrollLeft += cellRect.right - dataGrid.clientWidth
        }

        if (cellRect.left < firstColumnWidth) {
            nextScrollLeft -= firstColumnWidth - cellRect.left + 10
        }

        if (cellRect.top < headerRect.bottom) {
            nextScrollTop -= headerRect.bottom - cellRect.top
        }

        if (nextScrollTop === dataGrid.scrollTop && nextScrollLeft === dataGrid.scrollLeft) return

        // Apply the computed scroll offsets in one go.
        dataGrid.scrollTo({ top: nextScrollTop, left: nextScrollLeft })
    }, [cursor, firstColumnWidth, headerRef, dataGridRef, footerRef])
}
